import type { ComparisonCategory, PeerSearchResult, SampleConfidence } from '@/types/gmb';
import { formatComparisonCategorySourceLabel } from '@/pages/gmb-audit/utils/reportDisplay';

// UI-2B｜Peer Context（同業樣本脈絡帶）。
// 只讀 peerComparison / comparisonCategory；不重算中位數、不呼叫 Google。
// 作為「你和附近同業差在哪裡？」下方的樣本脈絡，取代原本偏長的同業摘要卡片。

const CONFIDENCE_LABEL: Record<SampleConfidence, string> = {
  HIGH: '高',
  MEDIUM: '中',
  LOW: '低',
  NONE: '不足',
};

/** 半徑顯示：整數公里省略小數（1.5 / 3 / 5 / 10 公里）。 */
function formatRadius(meters: number): string {
  const km = meters / 1000;
  return Number.isInteger(km) ? `${km} 公里` : `${km.toFixed(1)} 公里`;
}

function ContextItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="inline-flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-background-50 text-primary-700 ring-1 ring-background-300">
        <i className={`${icon} text-base`} />
      </span>
      <span className="text-sm leading-tight">
        <span className="block text-foreground-500">{label}</span>
        <span className="block font-bold text-foreground-900">{value}</span>
      </span>
    </div>
  );
}

export default function ReportPeerSummarySection({
  peerComparison,
  comparisonCategory,
}: {
  peerComparison?: PeerSearchResult;
  comparisonCategory: ComparisonCategory;
}) {
  if (!peerComparison) return null;

  const isServiceArea = peerComparison.mode === 'SERVICE_AREA';
  const confidence = CONFIDENCE_LABEL[peerComparison.sampleConfidence] ?? '不足';
  const catLabel = peerComparison.comparisonCategoryLabel || comparisonCategory.comparisonLabel || null;
  const sourceLabel = formatComparisonCategorySourceLabel(
    peerComparison.comparisonCategorySource ?? comparisonCategory.mode,
  );

  return (
    <section className="ds-container pt-4">
      <div className="rounded-2xl border border-background-200 bg-background-100 px-5 py-4 md:px-6 md:py-5">
        <p className="mb-3.5 flex items-center gap-2 text-sm font-bold text-foreground-800">
          <i className="ri-information-line text-base text-primary-700" />
          本次同業樣本脈絡
        </p>
        <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
          <ContextItem icon="ri-building-2-line" label="同業樣本" value={`${peerComparison.sampleCount} 家`} />
          <ContextItem icon="ri-shield-check-line" label="可信度" value={confidence} />
          <ContextItem
            icon={isServiceArea ? 'ri-map-2-line' : 'ri-focus-3-line'}
            label="模式"
            value={isServiceArea ? '服務區域' : '實體商圈'}
          />
          {isServiceArea ? (
            <ContextItem icon="ri-map-2-line" label="比較區域" value={peerComparison.comparisonArea || '服務區域'} />
          ) : (
            typeof peerComparison.radiusUsedM === 'number' && (
              <ContextItem icon="ri-focus-3-line" label="搜尋半徑" value={formatRadius(peerComparison.radiusUsedM)} />
            )
          )}
          {catLabel && <ContextItem icon="ri-price-tag-3-line" label="同業分類" value={catLabel} />}
          {sourceLabel && <ContextItem icon="ri-route-line" label="分類來源" value={sourceLabel} />}
        </div>
      </div>
    </section>
  );
}